import React, { useState, useEffect } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { notification, Tag } from 'antd';

import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    DeleteOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    EditOutlined,
    CheckOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import IBaseComponentProps from '@components/base-component-props';

import { exception, securityRestriction } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ICompany } from '@entities/company';

import { Permission, hasPermission } from '@enums/permission';
import { enumLabel as companyTypeLabel } from '@enums/company-type';

const Companies = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const navigate = useNavigate();
    const resolved = useResolvedPath('');
    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [companies, setCompanies] = useState<Array<ICompany>>([]);
    const [currentCompany, setCurrentCompany] = useState<ICompany>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);
        init();
    }, [refreshRequired]);

    const init = () => {
        setCompanies([]);

        setLoading(true);

        serverFetch('companies', { method: 'GET' })
            .then((data: any) => {
                setCompanies(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения компаний', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    };

    const onDelete = () => {
        if (!currentCompany) return;

        serverFetch(`companies/${currentCompany.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления уомпании', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCompanies)) {
                                securityRestriction(api, [Permission.ManageCompanies]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentCompany,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCompanies)) {
                                securityRestriction(api, [Permission.ManageCompanies]);
                                return;
                            }

                            navigate(`${resolved.pathname}/${currentCompany?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCompany,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCompany)) {
                                securityRestriction(api, [Permission.DeleteCompany]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить компанию "${currentCompany?.legalName}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = companies.find((o) => o.id == selectedRowKeys[0]);
            setCurrentCompany(entity);
        } else {
            setCurrentCompany(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ICompany> = [
            {
                title: 'Название',
                width: 250,
                render: (_, record) => {
                    return (
                        <>
                            <div>{record.legalName}</div>
                            <Tag color='var(--main-green)' variant='solid'>
                                {companyTypeLabel(record.type)}
                            </Tag>
                        </>
                    );
                },
            },
            {
                title: 'Реквизиты',
                width: 700,
                dataIndex: 'details',
            },
            {
                title: 'Основная',
                width: 90,
                align: 'center',
                render: (_: any, record) => {
                    return record.isMain ? <CheckCircleOutlined style={{ color: 'var(--main-green)' }} /> : '';
                },
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={companies}
                loading={tableLoading}
                onRow={(record: ICompany) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 175px)` }}
            />
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderTable()}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Companies;
